import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { BatchManagementService } from '@app/api/stock-service/api/batchManagement.service';
import { ItemControllerService } from '@app/api/order-service/api/itemController.service';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';

@Component({
  selector: 'app-lot-trace',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ButtonModule, InputTextModule, TableModule],
  templateUrl: './lot-trace.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotTraceComponent {
  private readonly batches = inject(BatchManagementService);
  private readonly items = inject(ItemControllerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  lotValue = '';
  loading = false;
  searched = false;
  lots: any[] = [];
  orders: any[] = [];

  search(): void {
    const lotValue = this.lotValue.trim();
    if (!lotValue) {
      return;
    }
    this.loading = true;
    this.batches
      .traceLots(lotValue)
      .pipe(
        switchMap((response) =>
          BlobConverterUtil.isBlob(response)
            ? BlobConverterUtil.convertToJson(response)
            : of(response),
        ),
        switchMap((lots: any[]) => {
          const rows = Array.isArray(lots) ? lots : [];
          const batchIds = rows.map((row) => row.batchId).filter((id: string) => !!id);
          if (batchIds.length === 0) {
            return of({ lots: rows, orders: [] as any[] });
          }
          return forkJoin(
            batchIds.map((batchId: string) =>
              this.items.lotUsages(batchId).pipe(
                switchMap((response) =>
                  BlobConverterUtil.isBlob(response)
                    ? BlobConverterUtil.convertToJson(response)
                    : of(response),
                ),
                catchError(() => of([])),
              ),
            ),
          ).pipe(
            switchMap((groups) =>
              of({
                lots: rows,
                orders: groups.flatMap((group) => (Array.isArray(group) ? group : [])),
              }),
            ),
          );
        }),
      )
      .subscribe({
        next: (result) => {
          this.lots = result.lots;
          this.orders = result.orders;
          this.searched = true;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.lots = [];
          this.orders = [];
          this.searched = true;
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  formatSourceType(sourceType?: string): string {
    if (!sourceType) {
      return '-';
    }
    const key = `lot_trace.source_types.${sourceType}`;
    const translated = this.translate.instant(key);
    return translated === key ? sourceType : translated;
  }

  formatDateTime(value?: string | Date): string {
    if (!value) {
      return '-';
    }
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) {
      return '-';
    }
    return d.toLocaleString('vi-VN');
  }
}
